import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, ref, withDefaults } from 'vue';
import { WindowCanvasConfig } from '@/interfaces/components/combinations/WindowCanvasConfig';
import { IndividualGuideTypes } from '@/modules/ConfiguratorCombinations/composables/useGuideImages';
import { DynamicCanvasSizes } from '@/modules/ConfiguratorCombinations/interfaces/CombinationsInterfaces';
import { useGuideImages } from '@/modules/ConfiguratorCombinations/composables/useGuideImages';
import { useWindowDescriptions } from '@/modules/ConfiguratorCombinations/composables/useWindowDescriptions';

interface CircleLetter {
    letter: string;
    visible: boolean;
}

interface Props {
    windowConfig: WindowCanvasConfig;
    index?: number;
    windowGuides: IndividualGuideTypes[1];
    guideType: string;
    secondGuideType: string;
    isFirstWindow?: boolean;
    isLastWindow?: boolean;
    combinationType?: string;
    isLastStep: boolean;
    widthDifference: number;
    totalProductsSelected: number;
    currentStep: number;
    currentProductFormId: number;
    canvasTemplateConfiguration: DynamicCanvasSizes;
    circleLetters?: CircleLetter[];
    useSeparateBoxes: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CombinationsConfiguratorCanvas',
  props: {
    windowConfig: null,
    index: null,
    windowGuides: null,
    guideType: null,
    secondGuideType: null,
    isFirstWindow: { type: Boolean, default: false },
    isLastWindow: { type: Boolean, default: false },
    combinationType: { default: '' },
    isLastStep: { type: Boolean },
    widthDifference: null,
    totalProductsSelected: null,
    currentStep: null,
    currentProductFormId: null,
    canvasTemplateConfiguration: null,
    circleLetters: { default: () => [] },
    useSeparateBoxes: { type: Boolean }
  },
  setup(__props: any, { expose }) {

const props = __props as { windowConfig: WindowCanvasConfig, index?: number, windowGuides: IndividualGuideTypes[1], guideType: string, secondGuideType: string, isFirstWindow: boolean, isLastWindow: boolean, combinationType: string, isLastStep: boolean, widthDifference: number, totalProductsSelected: number, currentStep: number, currentProductFormId: number, canvasTemplateConfiguration: DynamicCanvasSizes, circleLetters: CircleLetter[], useSeparateBoxes: boolean };



const { proxy } = getCurrentInstance()!;

// GLOBAL PROPERTIES AND REFS - used across different composables and for general window configuration

// Template refs
const windowGroup = ref<any>(null);
const dividerLine = ref<any>(null);

// Rolette specific properties
const slatHeight = 15;
const railWidth = 20;
const headerHeight = 40;
const openSectionHeight = 100;
const bottomLineHeight = 5;
const grayColor = '#cccccc';

const topViewDescription: any = proxy.$t('Top-down View');
const windowDescription: any = proxy.$t('View From the Inside Out');

const transformedDimensions = computed<{ height: number; width: number }>(() => {
    return {
        height: parseFloat(props.windowConfig.height as any) * 10,
        width: parseFloat(props.windowConfig.width as any) * 10,
    };
});

const groupScaleX = computed(() => 0.42);
const groupScaleY = computed(() => 0.42);

const frameWidth = computed(() => transformedDimensions.value?.width * groupScaleX.value);

const windowPosition = computed(() => {
    return props.windowConfig?.position ? parseInt(props.windowConfig.position.toString(), 10) : null;
});

// DESCRIPTION COMPOSABLE - handles logic for showing/hiding descriptions and calculating their positions based on config and window position

const { descriptionYOffset, descriptionTextConfig, textBackgroundConfig } = useWindowDescriptions({
    topViewDescription: () => topViewDescription,
    windowDescription: () => windowDescription,
    canvasTemplateConfiguration: () => props.canvasTemplateConfiguration,
});

// GUIDE IMAGES COMPOSABLE - handles logic for showing/hiding guide images and calculating their configs based on guide types, window position, step, and other relevant config

const {
    isFirstGuideSingle,
    isSecondGuideSingle,
    areBothGuidesSingle,
    isFirstGuidePartition,
    isSecondGuidePartition,
    areBothGuidesPartition,
    isAnyGuideSelected,
    shouldShowPartitionGuide,
    showLeftGuideImage,
    showRightGuideImage,
    showMiddleGuideImage,
    leftGuideImageConfig,
    rightGuideImageConfig,
    middleGuideImageConfig,
} = useGuideImages({
    guideType: () => props.guideType,
    secondGuideType: () => props.secondGuideType,
    isFirstWindow: () => props.isFirstWindow,
    isLastWindow: () => props.isLastWindow,
    isLastStep: () => props.isLastStep,
    windowPosition: () => windowPosition.value,
    frameWidth: () => frameWidth.value,
    windowGuides: () => props.windowGuides,
    canvasTemplateConfiguration: () => props.canvasTemplateConfiguration,
});

// EXTRA SPACE CALCULATIONS - handles logic for calculating extra space on the sides of the window when using extra width, as well as calculating positions for different elements based on window position and config

const windowGroupYOffset = computed<number>(() => {
    return props.canvasTemplateConfiguration.windowGroupYOffset ?? 320;
});

const groupX = computed(() => {
    return props.windowConfig.positionX !== null ? props.windowConfig.positionX : props.windowConfig.initialPositionX;
});

const groupY = computed(() => {
    return props.windowConfig.positionY !== null ? props.windowConfig.positionY : props.windowConfig.initialPositionY;
});

const positionTextConfig = computed(() => {
    const frameWidth = transformedDimensions.value?.width * groupScaleX.value;
    const position = props.windowConfig.position?.toString() || '';

    return {
        x: frameWidth / 2,
        y: 30,
        text: position,
        fontSize: 24,
        fontFamily: 'Arial, Helvetica, sans-serif',
        fontStyle: 'bold',
        fontVariant: 'normal',
        fill: '#333333',
        align: 'center',
        verticalAlign: 'middle',
        perfectDrawEnabled: false,
        shadowForStrokeEnabled: false,
        hitStrokeWidth: 0,
        listening: false,
        transformsEnabled: 'position',
        visible: true,
    };
});

const isUsingExtraWidth = computed(() => props.widthDifference > 0);

const extraWidth = computed(() => {
    if (isUsingExtraWidth.value) {
        return Math.floor(props.widthDifference / 2);
    }

    return 0;
});

const isStepThreeOrHigher = computed(() => props.currentStep >= 3);

const slats = computed(() => {
    const frameHeight = transformedDimensions.value?.height * groupScaleY.value;
    const frameWidth = transformedDimensions.value?.width * groupScaleX.value;
    const result = [];

    const availableHeight = frameHeight - headerHeight - openSectionHeight;
    const totalSlats = Math.floor(availableHeight / slatHeight);

    for (let i = 0; i < totalSlats; i++) {
        result.push({
            y: headerHeight + i * slatHeight,
            width: frameWidth,
            height: slatHeight - 2,
        });
    }

    return result;
});

const roletteConfigData = computed(() => {
    const blueFill = '#1a53ff';
    const blueOutline = '#0033cc';

    return {
        leftRailColor: showLeftGuideImage.value && props.currentStep === 4 ? blueFill : grayColor,
        leftRailOutline: showLeftGuideImage.value && props.currentStep === 4 ? blueOutline : grayColor,
        rightRailColor: showRightGuideImage.value && props.currentStep === 4 ? blueFill : grayColor,
        rightRailOutline: showRightGuideImage.value && props.currentStep === 4 ? blueOutline : grayColor,
    };
});

function roletteConfig(type: string) {
    const frameWidth = transformedDimensions.value?.width * groupScaleX.value;
    const frameHeight = transformedDimensions.value?.height * groupScaleY.value;

    const { leftRailColor, leftRailOutline, rightRailColor, rightRailOutline } = roletteConfigData.value;

    switch (type) {
        case 'header':
            return {
                x: 0,
                y: 0,
                width: frameWidth,
                height: headerHeight,
                fill: '#cccccc',
                stroke: '#999999',
                strokeWidth: 1,
            };
        case 'leftRail':
            return {
                x: 0,
                y: 0,
                width: railWidth,
                height: frameHeight,
                fill: leftRailColor,
                stroke: leftRailOutline,
                strokeWidth: 1,
            };
        case 'rightRail':
            return {
                x: frameWidth - railWidth,
                y: 0,
                width: railWidth,
                height: frameHeight,
                fill: rightRailColor,
                stroke: rightRailOutline,
                strokeWidth: 1,
            };
        case 'windowOpening':
            return {
                x: 0 + railWidth * 3.5,
                y: frameHeight - openSectionHeight * 1.2,
                width: frameWidth - railWidth * 7,
                height: openSectionHeight * 0.9,
                fill: 'white',
                stroke: '#aaaaaa',
                strokeWidth: 1,
            };
        case 'bottomLine':
            return {
                x: 0,
                y: frameHeight - bottomLineHeight,
                width: frameWidth,
                height: bottomLineHeight,
                fill: '#666666',
                stroke: '#444444',
                strokeWidth: 1,
            };
        default:
            return {};
    }
}

const slatsBackgroundConfig = computed(() => {
    const frameWidth = transformedDimensions.value?.width * groupScaleX.value;
    const frameHeight = transformedDimensions.value?.height * groupScaleY.value;
    const availableHeight = frameHeight - headerHeight - openSectionHeight - slatHeight;

    return {
        x: railWidth,
        y: headerHeight,
        width: frameWidth - railWidth * 2,
        height: availableHeight,
        fill: '#444444',
        stroke: '#333333',
        strokeWidth: 0.5,
    };
});

function slatConfig(slat: any) {
    return {
        x: 0,
        y: slat.y,
        width: slat.width,
        height: slat.height,
        fill: '#e0e0e0',
        stroke: '#cccccc',
        strokeWidth: 0.5,
    };
}

const colorConfig = computed(() => {
    const yellowColor = '#FFD700';
    const outlineColor = '#FFC107';

    return {
        lineColor: shouldShowPartitionGuide.value ? yellowColor : grayColor,
        strokeColor: shouldShowPartitionGuide.value ? outlineColor : grayColor,
    };
});

const rectangleConfig = computed(() => {
    const frameWidth = transformedDimensions.value?.width * groupScaleX.value;

    return {
        x: 0,
        y: 0,
        width: frameWidth,
        height: 70,
        fill: grayColor,
        stroke: '#333333',
        strokeWidth: 1,
        cornerRadius: 4,
        perfectDrawEnabled: true,
        shadowForStrokeEnabled: false,
        strokeScaleEnabled: false,
    };
});

const showExtraWindowWidth = computed(() => {
    return isUsingExtraWidth.value && isStepThreeOrHigher.value && props.totalProductsSelected >= 2;
});

const dashedExtraWidthConfig = computed(() => {
    const frameWidth = transformedDimensions.value?.width * groupScaleX.value;
    const extraWidthValue = extraWidth.value * 10 * groupScaleX.value;

    if (props.isFirstWindow) {
        return {
            x: -extraWidthValue,
            y: 0,
            width: extraWidthValue,
            height: 70,
            stroke: '#1a53ff',
            strokeWidth: 1,
            dash: [3, 3],
            fill: 'rgba(230, 240, 255, 1)',
            perfectDrawEnabled: true,
            shadowForStrokeEnabled: false,
            strokeScaleEnabled: false,
        };
    } else if (props.isLastWindow) {
        return {
            x: frameWidth,
            y: 0,
            width: extraWidthValue,
            height: 70,
            stroke: '#1a53ff',
            strokeWidth: 1,
            dash: [3, 3],
            fill: 'rgba(230, 240, 255, 1)',
            perfectDrawEnabled: true,
            shadowForStrokeEnabled: false,
            strokeScaleEnabled: false,
        };
    }

    return {
        visible: false,
    };
});

const circlesConfig = computed(() => {
    const frameWidth = transformedDimensions.value?.width * groupScaleX.value;
    const circleRadius = 22;
    const spacing = 40;
    const startX = 30;

    const defaultCircles = [
        { letter: 'L', visible: true },
        { letter: 'D', visible: false },
    ];

    const circles = props.circleLetters && props.circleLetters.length > 0 ? props.circleLetters : defaultCircles;

    return circles.map((item, idx) => {
        let x;
        switch (item.letter) {
            case 'L':
                x = startX;
                if (props.isFirstWindow && isUsingExtraWidth.value) {
                    x = startX;
                }
                break;
            case 'D':
                x = frameWidth - startX;
                if (props.isLastWindow && isUsingExtraWidth.value) {
                    x = frameWidth - startX;
                }
                break;
            default:
                x = startX + idx * spacing;
                if (props.isFirstWindow && isUsingExtraWidth.value) {
                    x = x;
                }
                break;
        }

        return {
            visible: item.visible,
            config: {
                x,
                y: 33,
                radius: circleRadius,
                fill: '#1a53ff',
                stroke: '#0033cc',
                strokeWidth: 1,
                perfectDrawEnabled: true,
                shadowForStrokeEnabled: false,
                hitStrokeWidth: 0,
                strokeScaleEnabled: false,
            },
            textConfig: {
                x: x - 5,
                y: 25,
                text: item.letter,
                fontSize: 20,
                fontFamily: 'Arial',
                fill: 'white',
                align: 'center',
                perfectDrawEnabled: true,
                shadowForStrokeEnabled: false,
                lineJoin: 'round',
                fontStyle: 'bold',
            },
        };
    });
});

const verticalLineConfig = computed(() => {
    const frameWidth = transformedDimensions.value?.width * groupScaleX.value;
    const frameHeight = transformedDimensions.value?.height * groupScaleY.value;
    const lineWidth = 65;
    const offset = 30;

    const { lineColor, strokeColor } = colorConfig.value;

    return {
        x: frameWidth - offset,
        y: headerHeight,
        width: lineWidth,
        height: frameHeight - headerHeight,
        fill: lineColor,
        stroke: strokeColor,
        strokeWidth: 1,
        opacity: 1,
    };
});

// Public method to allow the parent component to move this window group to top
function moveWindowToTop(): boolean {
    if (windowGroup.value && dividerLine.value) {
        const node = (windowGroup.value as any)._konvaNode;
        if (node) {
            node.moveToTop();
            return true;
        }
    }

    return false;
}

expose({ moveWindowToTop });

return { __sfc: true,props, proxy, windowGroup, dividerLine, slatHeight, railWidth, headerHeight, openSectionHeight, bottomLineHeight, grayColor, topViewDescription, windowDescription, transformedDimensions, groupScaleX, groupScaleY, frameWidth, windowPosition, descriptionYOffset, descriptionTextConfig, textBackgroundConfig, isFirstGuideSingle, isSecondGuideSingle, areBothGuidesSingle, isFirstGuidePartition, isSecondGuidePartition, areBothGuidesPartition, isAnyGuideSelected, shouldShowPartitionGuide, showLeftGuideImage, showRightGuideImage, showMiddleGuideImage, leftGuideImageConfig, rightGuideImageConfig, middleGuideImageConfig, windowGroupYOffset, groupX, groupY, positionTextConfig, isUsingExtraWidth, extraWidth, isStepThreeOrHigher, slats, roletteConfigData, roletteConfig, slatsBackgroundConfig, slatConfig, colorConfig, rectangleConfig, showExtraWindowWidth, dashedExtraWidthConfig, circlesConfig, verticalLineConfig, moveWindowToTop }
}

})